import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getModuleMode } from '@/lib/feature-mode';
import type {
  MaintenanceTransaction,
  CreateMaintenanceDto,
  UpdateMaintenanceDto,
  MaintenanceSummary,
  MaintenanceFilters,
  MaintenanceType,
} from '@/types/maintenance';

interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const DEMO_STORAGE_KEY = 'maintenance_demo_data';

const getDataSource = (): 'demo' | 'api' => {
  const mode = getModuleMode('assets');
  if (mode === 'demo') return 'demo';
  return 'api';
};

const generateDemoMaintenanceData = (): MaintenanceTransaction[] => {
  const maintenanceTypes: MaintenanceType[] = ['Repair', 'Service', 'Inspection', 'Upgrade', 'Calibration', 'Cleaning', 'Replacement', 'Other'];
  const suppliers = ['TechServ Ltd', 'MaintenancePro', 'QuickFix Services', 'Industrial Solutions', 'Premier Maintenance', null];
  const descriptions = [
    'Annual service and inspection',
    'Emergency repair - motor failure',
    'Scheduled calibration check',
    'Software upgrade and configuration',
    'Deep cleaning and sanitization',
    'Replacement of worn components',
    'Safety inspection and certification',
    'Preventive maintenance check',
    'Performance optimization',
    'Filter replacement and cleaning',
  ];
  const workPerformed = [
    'Replaced worn bearings and lubricated all moving parts',
    'Diagnosed and repaired electrical fault in control panel',
    'Calibrated sensors and verified accuracy within specifications',
    'Updated firmware to latest version and tested all functions',
    'Cleaned all components and replaced air filters',
    'Replaced drive belt and adjusted tension',
    'Inspected safety systems and updated certification',
    'Checked fluid levels and replaced worn seals',
    'Optimized settings for improved efficiency',
    'Replaced filters and cleaned intake vents',
  ];

  const transactions: MaintenanceTransaction[] = [];
  const now = new Date();

  for (let i = 0; i < 25; i++) {
    const daysAgo = Math.floor(Math.random() * 365);
    const maintenanceDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000);
    const maintenanceType = maintenanceTypes[Math.floor(Math.random() * maintenanceTypes.length)];
    const isWarranty = Math.random() < 0.15;
    const net = isWarranty ? 0 : Math.floor(Math.random() * 2000) + 50;
    const taxRate = isWarranty ? 0 : 20;
    const tax = net * (taxRate / 100);
    const gross = net + tax;
    const supplier = suppliers[Math.floor(Math.random() * suppliers.length)];
    const descIndex = Math.floor(Math.random() * descriptions.length);
    const nextMaintenanceDate = Math.random() < 0.6 
      ? new Date(now.getTime() + Math.floor(Math.random() * 180 + 30) * 24 * 60 * 60 * 1000).toISOString()
      : undefined;

    transactions.push({
      id: `maint-demo-${i + 1}`,
      assetId: String(Math.floor(Math.random() * 20) + 1),
      assetCode: `AST-${String(Math.floor(Math.random() * 20) + 1).padStart(6, '0')}`,
      assetName: `Demo Asset ${Math.floor(Math.random() * 20) + 1}`,
      reference: `MAINT-${now.getFullYear()}-${String(i + 1).padStart(4, '0')}`,
      maintenanceDate: maintenanceDate.toISOString(),
      maintenanceType,
      net,
      tax,
      taxRate,
      gross,
      currency: 'GBP',
      supplierName: supplier ?? undefined,
      supplierReference: supplier ? `INV-${String(Math.floor(Math.random() * 10000)).padStart(5, '0')}` : undefined,
      description: descriptions[descIndex],
      notes: Math.random() < 0.3 ? 'Follow-up inspection recommended in 3 months' : undefined,
      workPerformed: workPerformed[descIndex],
      partsReplaced: Math.random() < 0.4 ? 'Various consumable parts' : undefined,
      nextMaintenanceDate,
      warrantyWork: isWarranty,
      createdBy: 'demo-user',
      createdDate: maintenanceDate.toISOString(),
      modifiedBy: undefined,
      modifiedDate: undefined,
    });
  }

  return transactions.sort((a, b) => 
    new Date(b.maintenanceDate).getTime() - new Date(a.maintenanceDate).getTime()
  );
};

const getDemoData = (): MaintenanceTransaction[] => {
  const stored = localStorage.getItem(DEMO_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // Fall through to generate new data
    }
  }
  const data = generateDemoMaintenanceData();
  localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(data));
  return data;
};

const saveDemoData = (data: MaintenanceTransaction[]): void => {
  localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(data));
};

const applyDemoFilters = (data: MaintenanceTransaction[], filters?: MaintenanceFilters): MaintenanceTransaction[] => {
  if (!filters) return data;
  
  let filtered = data;
  
  if (filters.assetId) {
    filtered = filtered.filter(t => t.assetId === filters.assetId);
  }
  if (filters.maintenanceTypes?.length) {
    filtered = filtered.filter(t => filters.maintenanceTypes!.includes(t.maintenanceType));
  }
  if (filters.dateFrom) {
    filtered = filtered.filter(t => new Date(t.maintenanceDate) >= new Date(filters.dateFrom!));
  }
  if (filters.dateTo) {
    filtered = filtered.filter(t => new Date(t.maintenanceDate) <= new Date(filters.dateTo!));
  }
  if (filters.costMin !== undefined) {
    filtered = filtered.filter(t => t.gross >= filters.costMin!);
  }
  if (filters.costMax !== undefined) {
    filtered = filtered.filter(t => t.gross <= filters.costMax!);
  }
  if (filters.supplierName) {
    filtered = filtered.filter(t => t.supplierName?.toLowerCase().includes(filters.supplierName!.toLowerCase()));
  }
  if (filters.warrantyWork !== undefined) {
    filtered = filtered.filter(t => t.warrantyWork === filters.warrantyWork);
  }
  
  return filtered;
};

const buildApiQueryParams = (filters: MaintenanceFilters): URLSearchParams => {
  const params = new URLSearchParams();
  
  if (filters.assetId) params.append('assetId', filters.assetId);
  if (filters.maintenanceTypes?.length) {
    filters.maintenanceTypes.forEach(type => params.append('maintenanceTypes', type));
  }
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  if (filters.costMin !== undefined) params.append('costMin', filters.costMin.toString());
  if (filters.costMax !== undefined) params.append('costMax', filters.costMax.toString());
  if (filters.supplierName) params.append('supplierName', filters.supplierName);
  if (filters.warrantyWork !== undefined) params.append('warrantyWork', filters.warrantyWork.toString());
  if (filters.pageNumber) params.append('pageNumber', filters.pageNumber.toString());
  if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);
  
  return params;
};

class MaintenanceService {
  private getBaseUrl(): string {
    return getBackendUrl();
  }

  async getAll(filters?: MaintenanceFilters): Promise<PaginatedResponse<MaintenanceTransaction>> {
    if (getDataSource() === 'demo') {
      const data = applyDemoFilters(getDemoData(), filters);
      const pageNumber = filters?.pageNumber ?? 1;
      const pageSize = filters?.pageSize ?? 10;
      const totalCount = data.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      const items = data.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

      return { items, totalCount, pageNumber, pageSize, totalPages };
    }

    const baseUrl = this.getBaseUrl();
    const params = filters ? buildApiQueryParams(filters) : new URLSearchParams();
    const queryString = params.toString();
    const endpoint = `${baseUrl}/api/maintenancetransactions`;
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    const response = await fetch(url, {
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch maintenance transactions');
    }
    
    return response.json();
  }

  async getById(id: string): Promise<MaintenanceTransaction> {
    if (getDataSource() === 'demo') {
      const data = getDemoData();
      const transaction = data.find(t => t.id === id);
      if (!transaction) {
        throw new Error('Maintenance transaction not found');
      }
      return transaction;
    }

    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/api/maintenancetransactions/${id}`, {
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch maintenance transaction');
    }
    
    return response.json();
  }

  async getByAssetId(assetId: string): Promise<MaintenanceTransaction[]> {
    if (getDataSource() === 'demo') {
      const data = getDemoData();
      return data.filter(t => t.assetId === assetId);
    }

    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/api/maintenancetransactions/asset/${assetId}`, {
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch asset maintenance transactions');
    }
    
    return response.json();
  }

  async getSummaryByAssetId(assetId: string): Promise<MaintenanceSummary> {
    if (getDataSource() === 'demo') {
      const data = getDemoData();
      const assetTransactions = data.filter(t => t.assetId === assetId);
      const totalGross = assetTransactions.reduce((sum, t) => sum + t.gross, 0);
      const totalEvents = assetTransactions.length;
      const lastMaintenanceDate = assetTransactions.length > 0
        ? assetTransactions.reduce((latest, t) => 
            new Date(t.maintenanceDate) > new Date(latest) ? t.maintenanceDate : latest,
            assetTransactions[0].maintenanceDate
          )
        : undefined;
      const nextScheduledDate = assetTransactions
        .filter(t => t.nextMaintenanceDate)
        .map(t => t.nextMaintenanceDate!)
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];

      return { totalGross, totalEvents, lastMaintenanceDate, nextScheduledDate };
    }

    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/api/maintenancetransactions/asset/${assetId}/summary`, {
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch maintenance summary');
    }
    
    return response.json();
  }

  async create(dto: CreateMaintenanceDto): Promise<MaintenanceTransaction> {
    if (getDataSource() === 'demo') {
      const data = getDemoData();
      const tax = dto.net * (dto.taxRate / 100);
      const gross = dto.net + tax;
      const newTransaction: MaintenanceTransaction = {
        id: `maint-demo-${Date.now()}`,
        assetId: dto.assetId,
        assetCode: `AST-${dto.assetId.padStart(6, '0')}`,
        assetName: `Demo Asset ${dto.assetId}`,
        reference: dto.reference,
        maintenanceDate: dto.maintenanceDate,
        maintenanceType: dto.maintenanceType as MaintenanceType,
        net: dto.net,
        tax,
        taxRate: dto.taxRate,
        gross,
        currency: 'GBP',
        supplierName: dto.supplierName,
        supplierReference: dto.supplierReference,
        description: dto.description,
        notes: dto.notes,
        workPerformed: dto.workPerformed,
        partsReplaced: dto.partsReplaced,
        nextMaintenanceDate: dto.nextMaintenanceDate,
        warrantyWork: dto.warrantyWork,
        createdBy: 'demo-user',
        createdDate: new Date().toISOString(),
        modifiedBy: undefined,
        modifiedDate: undefined,
      };
      data.unshift(newTransaction);
      saveDemoData(data);
      return newTransaction;
    }

    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/api/maintenancetransactions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(dto),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to create maintenance transaction');
    }
    
    return response.json();
  }

  async update(id: string, dto: UpdateMaintenanceDto): Promise<MaintenanceTransaction> {
    if (getDataSource() === 'demo') {
      const data = getDemoData();
      const index = data.findIndex(t => t.id === id);
      if (index === -1) {
        throw new Error('Maintenance transaction not found');
      }
      const tax = dto.net * (dto.taxRate / 100);
      const gross = dto.net + tax;
      const updated: MaintenanceTransaction = {
        ...data[index],
        reference: dto.reference,
        maintenanceDate: dto.maintenanceDate,
        maintenanceType: dto.maintenanceType as MaintenanceType,
        net: dto.net,
        tax,
        taxRate: dto.taxRate,
        gross,
        supplierName: dto.supplierName,
        supplierReference: dto.supplierReference,
        description: dto.description,
        notes: dto.notes,
        workPerformed: dto.workPerformed,
        partsReplaced: dto.partsReplaced,
        nextMaintenanceDate: dto.nextMaintenanceDate,
        warrantyWork: dto.warrantyWork,
        modifiedBy: 'demo-user',
        modifiedDate: new Date().toISOString(),
      };
      data[index] = updated;
      saveDemoData(data);
      return updated;
    }

    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/api/maintenancetransactions/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(dto),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update maintenance transaction');
    }
    
    return response.json();
  }

  async delete(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      const data = getDemoData();
      const index = data.findIndex(t => t.id === id);
      if (index === -1) {
        throw new Error('Maintenance transaction not found');
      }
      data.splice(index, 1);
      saveDemoData(data);
      return;
    }

    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/api/maintenancetransactions/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete maintenance transaction');
    }
  }

  async generateReference(): Promise<string> {
    if (getDataSource() === 'demo') {
      const data = getDemoData();
      const year = new Date().getFullYear();
      const prefix = `MAINT-${year}-`;
      const existingRefs = data
        .filter(t => t.reference.startsWith(prefix))
        .map(t => parseInt(t.reference.replace(prefix, ''), 10))
        .filter(n => !isNaN(n));
      const nextNumber = existingRefs.length > 0 ? Math.max(...existingRefs) + 1 : 1;
      return `${prefix}${String(nextNumber).padStart(4, '0')}`;
    }

    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/api/maintenancetransactions/generate-reference`, {
      credentials: 'include',
    });
    
    if (!response.ok) {
      throw new Error('Failed to generate reference');
    }
    
    return response.text();
  }
}

export const maintenanceService = new MaintenanceService();
